import React, { useState, useEffect, useCallback } from 'react';
import { Container } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaSearch, FaExclamationCircle, FaEye, FaChevronRight, FaPlus } from 'react-icons/fa';
import { formatDate } from '../utils/helpers';
import { noticeAPI } from '../utils/api';

const NoticeList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [inputValue, setInputValue] = useState(searchParams.get('search') || '');
  const itemsPerPage = 10;

  // 관리자 여부 (localStorage 토큰이 있으면 간단히 허용 — 필요 시 role 체크로 교체)
  const isAdmin = !!localStorage.getItem('accessToken');

  const fetchNotices = useCallback(async (page, search) => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, page_size: itemsPerPage };
      if (search) params.search = search;
      const res = await noticeAPI.getAll(params);
      const data = res.data;
      setNotices(data.results || []);
      setTotalPages(data.total_pages || 1);
      setTotalCount(data.total || 0);
    } catch (err) {
      setError('공지사항을 불러오는 중 오류가 발생했습니다.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotices(currentPage, searchTerm);
  }, [currentPage, searchTerm, fetchNotices]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    const params = new URLSearchParams(searchParams);
    params.set('page', page);
    setSearchParams(params);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchTerm(inputValue);
    setCurrentPage(1);
    const params = new URLSearchParams();
    if (inputValue) params.set('search', inputValue);
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleReset = () => {
    setInputValue('');
    setSearchTerm('');
    setCurrentPage(1);
    setSearchParams({ page: '1' });
  };

  // 페이지 번호 배열
  const getPageNums = () => {
    const total = Math.min(5, totalPages);
    let start = 1;
    if (totalPages > 5) {
      if (currentPage <= 3) start = 1;
      else if (currentPage >= totalPages - 2) start = totalPages - 4;
      else start = currentPage - 2;
    }
    return Array.from({ length: total }, (_, i) => start + i);
  };

  // 중요 공지 / 일반 공지 분리
  const importantNotices = notices.filter(n => n.is_important);
  const normalNotices    = notices.filter(n => !n.is_important);

  return (
    <div className="nl-wrap">
      {/* ── 히어로 ── */}
      <section className="nl-hero">
        <div className="nl-hero-dharma" aria-hidden="true">☸</div>
        <Container className="nl-hero-inner">
          <span className="nl-badge">공지사항</span>
          <h1 className="nl-hero-title">공지사항</h1>
          <p className="nl-hero-sub">
            비나이다의 중요 소식과 업데이트 내용을 확인하세요.
          </p>
        </Container>
      </section>

      <Container className="nl-container">

        {/* ── 검색 + 작성 버튼 ── */}
        <div className="nl-toolbar">
          <form onSubmit={handleSearch} className="nl-search-box">
            <div className="nl-search-field">
              <FaSearch className="nl-search-ico" />
              <input
                className="nl-input"
                type="text"
                placeholder="공지사항 검색…"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
              />
            </div>
            <button type="submit" className="nl-btn nl-btn--primary">검색</button>
            {(inputValue || searchTerm) && (
              <button type="button" className="nl-btn nl-btn--ghost" onClick={handleReset}>
                초기화
              </button>
            )}
          </form>
          {isAdmin && (
            <button
              className="nl-btn nl-btn--write"
              onClick={() => navigate('/notice/write')}
            >
              <FaPlus /> 공지 작성
            </button>
          )}
        </div>

        {/* 총 개수 */}
        {!loading && !error && (
          <div className="nl-count">
            총 <strong>{totalCount}</strong>개의 공지사항
            {searchTerm && <span className="nl-count-search"> · "{searchTerm}" 검색 결과</span>}
          </div>
        )}

        {/* ── 컨텐츠 ── */}
        {loading ? (
          <div className="nl-loading">
            <span className="nl-loading-dharma">☸</span>
            <p>공지사항을 불러오는 중입니다…</p>
          </div>
        ) : error ? (
          <div className="nl-error">
            <span>⚠ {error}</span>
          </div>
        ) : notices.length === 0 ? (
          <div className="nl-empty">
            <span className="nl-empty-dharma">☸</span>
            <p>{searchTerm ? '검색 결과가 없습니다.' : '등록된 공지사항이 없습니다.'}</p>
            {searchTerm && (
              <button className="nl-btn nl-btn--outline" onClick={handleReset}>
                전체 보기
              </button>
            )}
          </div>
        ) : (
          <>
            {/* 중요 공지 */}
            {importantNotices.length > 0 && (
              <div className="nl-section">
                {importantNotices.map(n => (
                  <div
                    key={n.id}
                    className="nl-row nl-row--important"
                    onClick={() => navigate(`/notice/${n.id}`)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && navigate(`/notice/${n.id}`)}
                  >
                    <span className="nl-important-badge">
                      <FaExclamationCircle /> 공지
                    </span>
                    <span className="nl-row-title">{n.title}</span>
                    <span className="nl-row-meta">
                      <span className="nl-row-date">{formatDate(n.created_at)}</span>
                      <span className="nl-row-view"><FaEye /> {n.view_count}</span>
                    </span>
                    <FaChevronRight className="nl-row-arrow" />
                  </div>
                ))}
              </div>
            )}

            {/* 구분선 */}
            {importantNotices.length > 0 && normalNotices.length > 0 && (
              <div className="nl-divider" />
            )}

            {/* 일반 공지 */}
            {normalNotices.length > 0 && (
              <div className="nl-section">
                {normalNotices.map((n, idx) => (
                  <div
                    key={n.id}
                    className="nl-row"
                    onClick={() => navigate(`/notice/${n.id}`)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && navigate(`/notice/${n.id}`)}
                  >
                    <span className="nl-num">
                      {totalCount - ((currentPage - 1) * itemsPerPage) - importantNotices.length - idx}
                    </span>
                    <span className="nl-row-title">{n.title}</span>
                    <span className="nl-row-meta">
                      <span className="nl-row-date">{formatDate(n.created_at)}</span>
                      <span className="nl-row-view"><FaEye /> {n.view_count}</span>
                    </span>
                    <FaChevronRight className="nl-row-arrow" />
                  </div>
                ))}
              </div>
            )}

            {/* ── 페이지네이션 ── */}
            {totalPages > 1 && (
              <div className="nl-pagination">
                <button className="nl-page-btn" onClick={() => handlePageChange(1)} disabled={currentPage === 1}>«</button>
                <button className="nl-page-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>‹</button>
                {getPageNums().map(n => (
                  <button
                    key={n}
                    className={`nl-page-btn${n === currentPage ? ' nl-page-btn--active' : ''}`}
                    onClick={() => handlePageChange(n)}
                  >{n}</button>
                ))}
                <button className="nl-page-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>›</button>
                <button className="nl-page-btn" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>»</button>
              </div>
            )}
          </>
        )}
      </Container>

      {/* ── 스타일 ── */}
      <style jsx="true">{`
        :root {
          --nl-teal: #009590;
          --nl-teal-dark: #007873;
          --nl-teal-pale: #e8f5f5;
          --nl-text: #2c3e35;
          --nl-muted: #6e8a84;
          --nl-border: #e4edec;
        }

        /* 히어로 */
        .nl-hero {
          position: relative;
          background: linear-gradient(160deg, #f0fafa 0%, #e4f5f4 60%, #d5efee 100%);
          padding: 72px 0 64px;
          text-align: center;
          overflow: hidden;
        }
        .nl-hero-dharma {
          position: absolute;
          font-size: 18rem;
          color: var(--nl-teal);
          opacity: 0.04;
          top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          pointer-events: none;
          line-height: 1;
        }
        .nl-hero-inner { position: relative; z-index: 2; }
        .nl-badge {
          display: inline-block;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.18em;
          color: var(--nl-teal);
          text-transform: uppercase;
          margin-bottom: 12px;
          padding: 5px 14px;
          border: 1px solid var(--nl-teal);
          border-radius: 2px;
        }
        .nl-hero-title {
          font-size: 2.6rem;
          font-weight: 700;
          color: var(--nl-teal);
          letter-spacing: -0.02em;
          margin: 10px 0 16px;
        }
        .nl-hero-sub {
          font-size: 1rem;
          color: var(--nl-muted);
          line-height: 1.9;
          margin: 0;
        }

        /* 컨테이너 */
        .nl-container {
          padding-top: 36px;
          padding-bottom: 64px;
          max-width: 860px;
        }

        /* 툴바 (검색 + 작성 버튼) */
        .nl-toolbar {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 12px;
        }

        /* 검색 */
        .nl-search-box {
          flex: 1;
          display: flex;
          gap: 10px;
          align-items: center;
        }
        .nl-search-field {
          flex: 1;
          position: relative;
        }
        .nl-search-ico {
          position: absolute;
          left: 13px; top: 50%;
          transform: translateY(-50%);
          color: var(--nl-muted);
          font-size: 0.82rem;
        }
        .nl-input {
          width: 100%;
          border: 1px solid var(--nl-border);
          border-radius: 3px;
          padding: 10px 12px 10px 36px;
          font-size: 0.9rem;
          color: var(--nl-text);
          outline: none;
          font-family: 'Noto Sans KR', sans-serif;
          transition: border-color 0.2s;
        }
        .nl-input:focus { border-color: var(--nl-teal); }

        /* 총 개수 */
        .nl-count {
          font-size: 0.82rem;
          color: var(--nl-muted);
          margin-bottom: 16px;
        }
        .nl-count strong { color: var(--nl-teal); }
        .nl-count-search { color: var(--nl-teal); }

        /* 버튼 */
        .nl-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 10px 20px;
          border-radius: 3px;
          font-size: 0.88rem;
          font-weight: 600;
          cursor: pointer;
          border: none;
          font-family: 'Noto Sans KR', sans-serif;
          transition: all 0.2s;
          white-space: nowrap;
        }
        .nl-btn--primary { background: var(--nl-teal); color: #fff; }
        .nl-btn--primary:hover { background: var(--nl-teal-dark); }
        .nl-btn--outline { background: transparent; color: var(--nl-teal); border: 1.5px solid var(--nl-teal); }
        .nl-btn--outline:hover { background: var(--nl-teal); color: #fff; }
        .nl-btn--ghost { background: #f3f4f6; color: #888; }
        .nl-btn--ghost:hover { background: #e8e9ec; }
        .nl-btn--write { background: var(--nl-teal-pale); color: var(--nl-teal); border: 1px solid var(--nl-teal); flex-shrink: 0; }
        .nl-btn--write:hover { background: var(--nl-teal); color: #fff; }

        /* 로딩 */
        .nl-loading {
          text-align: center;
          padding: 80px 0;
          color: var(--nl-muted);
        }
        .nl-loading-dharma {
          font-size: 2.8rem;
          display: block;
          margin-bottom: 14px;
          color: var(--nl-teal);
          opacity: 0.25;
          animation: nlSpin 6s linear infinite;
        }
        @keyframes nlSpin { to { transform: rotate(360deg); } }
        .nl-loading p { font-size: 0.9rem; }

        /* 에러 / 빈 결과 */
        .nl-error {
          padding: 20px;
          border: 1px solid #f5c6cb;
          border-left: 3px solid #e74c3c;
          border-radius: 3px;
          background: #fff8f8;
          color: #c0392b;
          font-size: 0.9rem;
        }
        .nl-empty {
          text-align: center;
          padding: 72px 20px;
          color: var(--nl-muted);
        }
        .nl-empty-dharma {
          font-size: 3rem;
          display: block;
          color: var(--nl-teal);
          opacity: 0.15;
          margin-bottom: 12px;
        }
        .nl-empty p { font-size: 0.9rem; margin-bottom: 20px; }

        /* 목록 섹션 */
        .nl-section {
          border: 1px solid var(--nl-border);
          border-radius: 4px;
          overflow: hidden;
          margin-bottom: 0;
        }

        /* 각 행 */
        .nl-row {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 16px 20px;
          border-bottom: 1px solid #f0f5f5;
          cursor: pointer;
          transition: background 0.18s ease;
          background: #fff;
        }
        .nl-row:last-child { border-bottom: none; }
        .nl-row:hover { background: rgba(0,149,144,0.03); }

        .nl-row--important {
          background: #fffef8;
        }
        .nl-row--important:hover { background: rgba(0,149,144,0.04); }

        /* 중요 배지 */
        .nl-important-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          flex-shrink: 0;
          font-size: 0.68rem;
          font-weight: 700;
          color: #c0392b;
          background: #fdecea;
          border: 1px solid #f5c6cb;
          padding: 2px 8px;
          border-radius: 2px;
          letter-spacing: 0.06em;
          white-space: nowrap;
        }

        /* 번호 */
        .nl-num {
          flex-shrink: 0;
          width: 28px;
          text-align: center;
          font-size: 0.8rem;
          color: var(--nl-muted);
        }

        /* 제목 */
        .nl-row-title {
          flex: 1;
          font-size: 0.92rem;
          font-weight: 500;
          color: var(--nl-text);
          line-height: 1.5;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .nl-row--important .nl-row-title { font-weight: 600; }

        /* 메타 (날짜 + 조회수) */
        .nl-row-meta {
          display: flex;
          align-items: center;
          gap: 14px;
          flex-shrink: 0;
        }
        .nl-row-date {
          font-size: 0.78rem;
          color: var(--nl-muted);
          white-space: nowrap;
        }
        .nl-row-view {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 0.78rem;
          color: var(--nl-muted);
          white-space: nowrap;
        }

        /* 화살표 */
        .nl-row-arrow {
          flex-shrink: 0;
          font-size: 0.72rem;
          color: #ccc;
          transition: color 0.18s, transform 0.18s;
        }
        .nl-row:hover .nl-row-arrow { color: var(--nl-teal); transform: translateX(3px); }

        /* 구분선 */
        .nl-divider {
          height: 8px;
          background: transparent;
        }

        /* 페이지네이션 */
        .nl-pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 6px;
          margin-top: 28px;
        }
        .nl-page-btn {
          width: 38px; height: 38px;
          border-radius: 3px;
          border: 1px solid var(--nl-border);
          background: #fff;
          color: var(--nl-muted);
          font-size: 0.88rem;
          cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: all 0.2s;
          font-family: 'Noto Sans KR', sans-serif;
        }
        .nl-page-btn:hover:not(:disabled) { border-color: var(--nl-teal); color: var(--nl-teal); }
        .nl-page-btn:disabled { opacity: 0.3; cursor: default; }
        .nl-page-btn--active {
          background: var(--nl-teal);
          border-color: var(--nl-teal);
          color: #fff;
          font-weight: 700;
        }

        /* 반응형 */
        @media (max-width: 640px) {
          .nl-hero { padding: 52px 0 44px; }
          .nl-hero-title { font-size: 2rem; }
          .nl-row-meta { display: none; }
          .nl-row { padding: 14px 16px; gap: 10px; }
          .nl-container { padding-top: 24px; padding-bottom: 48px; }
          .nl-toolbar { flex-direction: column; align-items: stretch; }
          .nl-btn--write { justify-content: center; }
        }
      `}</style>
    </div>
  );
};

export default NoticeList;